import { forwardRef, useState, type KeyboardEvent } from "react";
import { X } from "lucide-react";
import { cn } from "../utils/cn";
import { fieldClass } from "./fieldClass";

export interface ChipListFieldProps {
  /**
   * Id for the inline "add" input, so an external <Label htmlFor> resolves to
   * it. Without one a labelled chip list has a label pointing at nothing.
   */
  id?: string;
  /** Current chip values (controlled). */
  values: string[];
  onChange: (values: string[]) => void;
  /** Placeholder for the inline "add" input. */
  placeholder?: string;
  /** Accessible label for the add input. */
  addLabel?: string;
  /** Hint the on-screen keyboard / validation for numeric chips. */
  inputMode?: "text" | "numeric" | "decimal";
  /**
   * Shown, but neither addable nor removable.
   *
   * The input carries it too, so Backspace-removes-the-last-chip stops with
   * everything else — a disabled input receives no key events.
   */
  disabled?: boolean;
  className?: string;
  /** Merged into the add input's field look — to size it to denser neighbours. */
  inputClassName?: string;
}

/**
 * A variable-length list of short values rendered as removable chips plus an
 * inline input. Enter (or blur) commits the draft; Backspace on an empty input
 * removes the last chip; duplicates are ignored. Reusable for anomalous values
 * and cut-off lists.
 */
export const ChipListField = forwardRef<HTMLDivElement, ChipListFieldProps>(
  (
    {
      id,
      values,
      onChange,
      placeholder = "Agregar…",
      addLabel = "Agregar valor",
      inputMode = "text",
      disabled = false,
      className,
      inputClassName,
    },
    ref,
  ) => {
    const [draft, setDraft] = useState("");

    const commit = () => {
      const v = draft.trim();
      setDraft("");
      if (!v || values.includes(v)) return;
      onChange([...values, v]);
    };
    const removeAt = (i: number) =>
      onChange(values.filter((_, idx) => idx !== i));

    const onKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        e.preventDefault();
        commit();
      } else if (e.key === "Backspace" && draft === "" && values.length > 0) {
        removeAt(values.length - 1);
      }
    };

    return (
      <div
        ref={ref}
        className={cn("flex flex-wrap items-center gap-2", className)}
      >
        {values.map((v, i) => (
          <span
            key={`${v}-${i}`}
            // A chip keeps its own compact height. Matching the field's h-9
            // lines it up while the list fits on one line and costs a third
            // of the row's height per chip once it wraps — and a chip is not
            // a field: it reads as a value already entered.
            className="inline-flex items-center gap-1.5 rounded-md border border-border bg-muted/40 px-2 py-1 text-sm tabular-nums"
          >
            {v}
            <button
              type="button"
              aria-label={`Quitar ${v}`}
              disabled={disabled}
              onClick={() => removeAt(i)}
              className="rounded text-muted-foreground transition-colors hover:text-destructive focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:opacity-50"
            >
              <X className="size-3" aria-hidden />
            </button>
          </span>
        ))}
        <input
          id={id}
          type="text"
          disabled={disabled}
          value={draft}
          inputMode={inputMode}
          placeholder={placeholder}
          aria-label={addLabel}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={onKeyDown}
          onBlur={commit}
          // The same height, corners and colour as every other field: this used
          // to be dashed, transparent and two pixels shorter, which read as a
          // different kind of control sitting in the same row.
          className={cn(
            fieldClass,
            "w-auto min-w-[6rem] flex-1",
            inputClassName,
          )}
        />
      </div>
    );
  },
);
ChipListField.displayName = "ChipListField";
